import { useCallback, useEffect, useState } from 'react';

const KEY = 'lux.vatBasis';
const EVT = 'lux:vat-basis';
const read = () => (window.localStorage.getItem(KEY) === 'gross' ? 'gross' : 'net');

/** 'net' (ex VAT — the figure the business works to, default) or 'gross' (inc VAT). One choice shared across every cost screen. */
export function useVatBasis() {
  const [basis, setBasisState] = useState(read);
  useEffect(() => {
    const sync = () => setBasisState(read());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(EVT, sync); window.removeEventListener('storage', sync); };
  }, []);
  const setBasis = useCallback((b) => { window.localStorage.setItem(KEY, b === 'gross' ? 'gross' : 'net'); window.dispatchEvent(new Event(EVT)); }, []);
  return [basis, setBasis];
}

export const BASIS_LABEL = { net: 'ex VAT', gross: 'inc VAT' };

/** Pence for one ledger row on the chosen basis. Rows with no VAT split read the same either way. */
export const rowPence = (row, basis) => (basis === 'gross' || row?.net_pence == null ? row?.value_pence : row.net_pence) || 0;

/** Totals block for the chosen basis — the API returns ex VAT at the top level and the same shape inc VAT under `inc_vat`. */
export const totalsFor = (totals, basis) => (basis === 'gross' && totals?.inc_vat ? totals.inc_vat : totals) || {};

const OPTIONS = [['net', 'Ex VAT'], ['gross', 'Inc VAT']];

/** Segmented Ex VAT / Inc VAT switch. `light` renders on the Site Diary paper theme. */
export function VatBasisToggle({ basis, onChange, light = false, size = 'sm', className = '', testid = 'vat-basis-toggle' }) {
  const pad = size === 'xs' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]';
  const shell = light ? 'bg-white border-[#E7E2D8]' : 'bg-white/5 border-white/10';
  const on = light ? 'bg-[#1F2937] text-[#F5A623] shadow-sm' : 'bg-[#F5A623] text-[#1E2530] shadow-sm';
  const off = light ? 'text-[#6B6459] hover:text-[#1F2937]' : 'text-gray-400 hover:text-white';
  return (
    <div role="group" aria-label="VAT basis" title="Every cost figure on this screen follows this switch" className={`inline-flex items-center rounded-full border p-0.5 ${shell} ${className}`} data-testid={testid}>
      {OPTIONS.map(([key, label]) => (
        <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={basis === key}
          className={`rounded-full font-semibold uppercase tracking-wider transition-colors duration-150 ${pad} ${basis === key ? on : off}`}
          data-testid={`${testid}-${key}`}>
          {label}
        </button>
      ))}
    </div>
  );
}
